/**
 * Opis klikniętego elementu do pomiaru użycia — pełny ślad działań (decyzja
 * użytkownika 23.10.2026: „przyciski też musimy wiedzieć i generalnie
 * wszystko, co się tam dzieje”). Do 23.10 zapisywaliśmy tylko elementy
 * z `data-testid`; teraz każde kliknięcie w element interaktywny.
 *
 * ## Co bierzemy z elementu
 *
 * - **rola** — przycisk, odnośnik, zakładka, pozycja menu, pole wyboru,
 *   przełącznik, chip, opcja listy, wiersz listy (`ElementRole`);
 * - **etykieta** — stały napis interfejsu: `data-usage-label`, `aria-label`,
 *   tekst przycisku, zakładki, pozycji menu albo chipu, podpis pola wyboru
 *   i przełącznika, nazwa pola listy rozwijanej; odnośnik poza menu — tylko
 *   **wzorzec adresu**, do którego prowadzi (`→ /contractors/:id/show`);
 * - **sekcja** — `data-testid` przodka, tytuł okna dialogowego albo
 *   najbliższy nagłówek.
 *
 * ## Czego nie bierzemy nigdy
 *
 * Wartości pól tekstowych (pole tekstowe w ogóle nie ma etykiety), treści
 * komórek i wierszy list, tekstu odnośników poza menu, opcji list
 * rozwijanych (to bywa nazwa kontrahenta) ani tekstu czegokolwiek w **strefie
 * danych**: wiersz tabeli, pole z wartością na karcie, pole z wybranymi
 * wartościami. Kliknięcie wiersza listy to tylko „wiersz listy X” (i numer
 * rekordu, gdy wiersz otwiera kartę zlecenia albo prezlecenia — dopisuje go
 * rejestrator po przejściu na kartę). Nagłówki i tytuły okien są ucinane na
 * pierwszym myślniku albo dwukropku (dalej bywa numer albo nazwa), a z każdej
 * etykiety i sekcji wypadają słowa wyglądające na daną (`withoutDataWords`:
 * litery z cyframi i słowa z samych wielkich liter spoza skrótów interfejsu —
 * numery rejestracyjne, numery zleceń, nazwy firm). Liczby i daty zamienia
 * serwer na `{id}` / `{data}` (`usage-events.ts`, `normalizeLabel`) i odrzuca
 * napisy wyglądające na treść (e-mail, adres strony, za długie).
 *
 * Gdzie napis interfejsu niesie daną, element może podać własną etykietę
 * (`data-usage-label`) albo wyłączyć tekst (`data-usage-mask`).
 */

export type ElementRole =
  | 'button'
  | 'link'
  | 'tab'
  | 'menuitem'
  | 'checkbox'
  | 'switch'
  | 'radio'
  | 'row'
  | 'chip'
  | 'option'
  | 'combobox'
  | 'download'
  | 'other';

export interface ElementTrace {
  elementRole: ElementRole | null;
  label: string | null;
  section: string | null;
  /** Wiersz listy react-admina — rejestrator dopisze numer rekordu po przejściu na kartę. */
  rowResource?: string;
  /** Plik pobierany odnośnikiem `download` (eksport listy). */
  download?: boolean;
}

/** Elementy interaktywne, w które kliknięcie zapisujemy. */
export const INTERACTIVE_SELECTOR = [
  'a[href]',
  'a[download]',
  'button',
  'summary',
  'select',
  'input[type="checkbox"]',
  'input[type="radio"]',
  '[role="button"]',
  '[role="link"]',
  '[role="tab"]',
  '[role="menuitem"]',
  '[role="menuitemcheckbox"]',
  '[role="menuitemradio"]',
  '[role="checkbox"]',
  '[role="switch"]',
  '[role="radio"]',
  '[role="option"]',
  '[role="combobox"]',
  '.MuiChip-clickable',
  'tr.RaDatagrid-clickableRow',
  '[role="row"]',
].join(', ');

/**
 * Strefa danych — tekst stąd nigdy nie idzie do etykiety: wiersze tabel,
 * pola z wartością na kartach, pola z wybranymi wartościami (chipy
 * kontrahentów w polu wyboru), oznaczone ręcznie (`data-usage-data`).
 */
const DATA_REGION = [
  'tbody',
  '[role="row"]',
  '[role="rowgroup"]',
  '.RaDatagrid-row',
  '.RaLabeled-root',
  '.RaReferenceField-root',
  '.RaReferenceArrayField-root',
  '.MuiAutocomplete-root',
  '.MuiInputBase-root',
  '[data-usage-data]',
].join(', ');

const HEADING = 'h1, h2, h3, h4, h5, h6, [role="heading"], .MuiCardHeader-title, .MuiDialogTitle-root';

/** Najdłuższy tekst brany jako etykieta przycisku — dłuższy to zwykle treść. */
export const TEXT_LABEL_MAX = 60;
/** Najdłuższy nagłówek brany jako sekcja. */
export const SECTION_MAX = 50;

const clean = (s: string | null | undefined): string => (s ?? '').replace(/\s+/g, ' ').trim();

/** Skróty, które są napisem interfejsu, a nie daną (wielkie litery zostają). */
const UI_ACRONYMS = new Set([
  'OK', 'VIA', 'TMS', 'FTL', 'CS', 'ADR', 'CSV', 'PDF', 'XML', 'NBP', 'VAT', 'GPS', 'HERE', 'LDM', 'ETA', 'API',
  'TIMOCOM', 'NIP', 'PLN', 'EUR', 'KM', 'ID', 'NP', 'DE', 'PL',
]);
const LETTER = new RegExp('\\p{L}', 'u');
const LOWER = new RegExp('\\p{Ll}', 'u');
const EDGE_PUNCT = new RegExp('^[^\\p{L}\\d]+|[^\\p{L}\\d]+$', 'gu');

/**
 * Słowa, które wyglądają na daną, a nie napis interfejsu, wypadają z etykiety
 * i nagłówka: litery z cyframi (numer rejestracyjny „WGM12345”, numer
 * zlecenia „DEMO/2026/10/001”) i słowo z samych wielkich liter spoza skrótów
 * interfejsu („DEMO 003”, „TRANS-POL”). Same liczby zostają — serwer zamienia
 * je na `{id}`. Wykryte przy e2e 23.10.2026: nagłówek „Na jakiej podstawie
 * rekomendujemy DEMO 003” dawał numer rejestracyjny w sekcji.
 */
export function withoutDataWords(text: string): string {
  return clean(
    text
      .split(' ')
      .filter((w) => {
        const core = w.replace(EDGE_PUNCT, '');
        if (core === '') return true;
        const hasLetter = LETTER.test(core);
        if (hasLetter && /\d/.test(core)) return false;
        if (hasLetter && core.length >= 2 && !LOWER.test(core) && !UI_ACRONYMS.has(core)) return false;
        return true;
      })
      .join(' '),
  );
}

const inDataRegion = (el: Element): boolean => el.closest(DATA_REGION) !== null;

/** Tekst elementu (bez tekstu ukrytego dla czytników i bez ikon). */
function textOf(el: Element): string {
  return clean(el.textContent);
}

/** Etykieta z tekstu tylko wtedy, gdy krótka i jednowierszowa. */
function shortText(s: string): string | null {
  const t = withoutDataWords(clean(s.split('\n')[0]));
  return t !== '' && t.length <= TEXT_LABEL_MAX ? t : null;
}

/**
 * Nagłówek albo tytuł okna jako sekcja: do pierwszego myślnika, dwukropka
 * albo kreski (dalej bywa numer zlecenia albo nazwa), najwyżej 50 znaków;
 * napis bez małych liter (np. nazwa firmy wielkimi literami) odpada.
 */
export function headingText(raw: string | null | undefined): string | null {
  let t = clean(raw);
  const cut = t.search(/\s[—–-]\s|:\s|\s\|\s|\s·\s/);
  if (cut > 0) t = t.slice(0, cut);
  t = withoutDataWords(t);
  if (t === '' || t.length > SECTION_MAX || !/\p{Ll}/u.test(t)) return null;
  return t;
}

/** Rola elementu interaktywnego. */
export function roleOf(el: Element): ElementRole {
  const role = el.getAttribute('role');
  const tag = el.tagName.toLowerCase();
  if (tag === 'a' && el.hasAttribute('download')) return 'download';
  if (role === 'row' || (tag === 'tr' && el.classList.contains('RaDatagrid-clickableRow'))) return 'row';
  if (role === 'tab') return 'tab';
  if (role === 'menuitem' || role === 'menuitemcheckbox' || role === 'menuitemradio') return 'menuitem';
  if (role === 'option') return 'option';
  if (role === 'combobox' || tag === 'select') return 'combobox';
  if (role === 'switch' || el.closest('.MuiSwitch-root')) return 'switch';
  if (role === 'checkbox' || (tag === 'input' && (el as HTMLInputElement).type === 'checkbox')) return 'checkbox';
  if (role === 'radio' || (tag === 'input' && (el as HTMLInputElement).type === 'radio')) return 'radio';
  if (el.classList.contains('MuiChip-root')) return 'chip';
  if (role === 'link' || tag === 'a') return 'link';
  if (role === 'button' || tag === 'button' || tag === 'summary') return 'button';
  return 'other';
}

const VALUE_SEGMENT = (s: string) =>
  /^\d+$/.test(s) || /\d{3,}/.test(s) || s.length > 40 || !/^[A-Za-z0-9_.-]+$/.test(s);

/** Wzorzec adresu odnośnika aplikacji: `#/contractors/12/show` → `/contractors/:id/show`. */
export function hrefPattern(href: string | null): string | null {
  if (!href) return null;
  let path = href;
  const hash = path.indexOf('#');
  if (hash >= 0) path = path.slice(hash + 1);
  else if (/^[a-z]+:/i.test(path)) return null; // adres zewnętrzny — nic o nim nie zapisujemy
  path = path.split('?')[0];
  if (!path.startsWith('/')) return null;
  const parts = path.split('/').filter(Boolean).map((s) => {
    let d = s;
    try {
      d = decodeURIComponent(s);
    } catch {
      d = '%';
    }
    return VALUE_SEGMENT(d) ? ':id' : d;
  });
  return `/${parts.join('/')}`;
}

/** Podpis pola wyboru, przełącznika albo listy rozwijanej (napis formularza, nie wartość). */
function fieldCaption(el: Element): string | null {
  const own = el.closest('label');
  if (own) {
    const caption = own.querySelector('.MuiFormControlLabel-label');
    const t = shortText(textOf(caption ?? own));
    if (t) return t;
  }
  const labelledBy = el.getAttribute('aria-labelledby');
  if (labelledBy) {
    const ids = labelledBy.split(/\s+/);
    // Lista rozwijana MUI: pierwszy identyfikator to podpis pola, drugi — wybrana wartość.
    const first = ids.length ? el.ownerDocument.getElementById(ids[0]) : null;
    if (first && first !== el && first.tagName.toLowerCase() === 'label') return shortText(textOf(first));
  }
  const control = el.closest('.MuiFormControl-root');
  const label = control?.querySelector(':scope > label, :scope > .MuiFormLabel-root');
  return label ? shortText(textOf(label)) : null;
}

/** Etykieta elementu — patrz opis modułu. */
export function labelOf(el: Element, role: ElementRole, rowResource?: string): string | null {
  if (role === 'row') return rowResource ? `wiersz listy ${rowResource}` : 'wiersz listy';
  const explicit = el.getAttribute('data-usage-label');
  if (explicit) return clean(explicit) || null;
  if (el.closest('[data-usage-mask]')) return null;
  if (role === 'download') {
    const name = el.getAttribute('download') ?? '';
    const ext = /\.([A-Za-z0-9]{1,5})$/.exec(name)?.[1]?.toLowerCase();
    return ext ? `plik .${ext}` : 'plik';
  }
  if (role === 'option') return null; // treść opcji to często nazwa kontrahenta, kierowcy, pojazdu
  const dataRegion = inDataRegion(el);
  const aria = clean(el.getAttribute('aria-label'));
  // W strefie danych — tylko aria-label przycisku (dymek ikony: „Usuń wariant”).
  if (aria && (!dataRegion || role === 'button' || role === 'checkbox' || role === 'switch')) {
    return withoutDataWords(aria) || null;
  }
  if (role === 'combobox' || role === 'checkbox' || role === 'switch' || role === 'radio') {
    return fieldCaption(el);
  }
  if (dataRegion) return null;
  if (role === 'link') {
    // Odnośnik w menu bocznym — nazwa pozycji menu; inny — dokąd prowadzi, bez tekstu.
    const pattern = hrefPattern(el.getAttribute('href'));
    return pattern ? `→ ${pattern}` : null;
  }
  if (role === 'button' || role === 'tab' || role === 'menuitem' || role === 'chip') {
    return shortText(textOf(el));
  }
  return null;
}

/** Sekcja: `data-testid` przodka, tytuł okna albo najbliższy nagłówek. */
export function sectionOf(el: Element, skipTestId: string | null, isIconAutoId: (e: Element) => boolean): string | null {
  let child: Element = el;
  let node: Element | null = el.parentElement;
  for (let depth = 0; node && depth < 30; depth += 1) {
    const testId = node.getAttribute('data-testid');
    if (testId && testId !== skipTestId && !isIconAutoId(node)) return testId;
    const usage = node.getAttribute('data-usage-section');
    if (usage) return clean(usage) || null;
    if (node.getAttribute('role') === 'dialog') return dialogTitle(node);
    if (!inDataRegion(node)) {
      // Nagłówek przed gałęzią z klikniętym elementem.
      let prev = child.previousElementSibling;
      for (let i = 0; prev && i < 6; i += 1, prev = prev.previousElementSibling) {
        const h = prev.matches(HEADING) ? prev : prev.querySelector(`:scope > ${HEADING}`);
        if (h) return headingText(textOf(h));
      }
    }
    child = node;
    node = node.parentElement;
  }
  return null;
}

/** Tytuł okna dialogowego (`aria-labelledby` albo nagłówek okna) — tą samą regułą co nagłówek. */
export function dialogTitle(dialog: Element): string | null {
  const id = dialog.getAttribute('aria-labelledby');
  const byId = id ? dialog.ownerDocument.getElementById(id.split(/\s+/)[0]) : null;
  const title = byId ?? dialog.querySelector('.MuiDialogTitle-root, h2, [role="heading"]');
  return title ? headingText(textOf(title)) : null;
}

/**
 * Opis klikniętego elementu interaktywnego albo `null` (kliknięcie w tło,
 * tekst, pole tekstowe bez identyfikatora). `resource` — pierwszy człon
 * bieżącej trasy (do „wiersz listy X”).
 */
export function traceOf(
  target: Element,
  resource: string,
  skipTestId: string | null,
  isIconAutoId: (e: Element) => boolean,
): (ElementTrace & { element: Element }) | null {
  const el = target.closest(INTERACTIVE_SELECTOR);
  if (!el) return null;
  const elementRole = roleOf(el);
  const rowResource = elementRole === 'row' && resource ? resource : undefined;
  return {
    element: el,
    elementRole,
    label: labelOf(el, elementRole, rowResource),
    section: elementRole === 'row' ? null : sectionOf(el, skipTestId, isIconAutoId),
    ...(rowResource ? { rowResource } : {}),
    ...(elementRole === 'download' ? { download: true } : {}),
  };
}
